import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { formatIndianCurrency, formatIndianDate } from '../../utils/formatters';
import { 
  Wallet, 
  TrendingUp, 
  Users, 
  Calendar,
  ArrowUp,
  ArrowDown,
  BarChart3,
  AlertCircle
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [arbitrageData, setArbitrageData] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchArbitrageData, 5000); // 5 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [walletRes, depositsRes, arbitrageRes] = await Promise.all([
        api.get('/wallets/me'),
        api.get('/deposits'),
        api.get('/arbitrage/opportunities'),
      ]);

      setWallet(walletRes.data);
      setDeposits(depositsRes.data);
      setArbitrageData(Array.isArray(arbitrageRes.data) ? arbitrageRes.data.slice(0, 5) : []);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
      setLoading(false);
    }
  };

  const fetchArbitrageData = async () => {
    try {
      const res = await api.get('/arbitrage/opportunities');
      console.log("response ------------", res);
      setArbitrageData(Array.isArray(res.data) ? res.data.slice(0, 5) : []);
    } catch (err) {
      console.error('Failed to fetch arbitrage data:', err);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  // Calculate total portfolio
  const totalDeposits = deposits
    .filter(d => d.status === 'active' || d.status === 'pending')
    .reduce((sum, d) => sum + d.amount, 0);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading font-bold text-gray-900" data-testid="dashboard-title">
            Welcome, {user?.name}!
          </h1>
          <p className="text-gray-600 mt-1">Here's your investment overview</p>
        </div>

        {/* KYC Alert */}
        {user?.kycStatus !== 'Approved' && (
          <div className='bg-warning-light border border-warning rounded-lg p-4' data-testid='kyc-alert'>
            <div className='flex items-start gap-3'>
              <AlertCircle className='w-5 h-5 text-warning flex-shrink-0 mt-0.5' />
              <div>
                <h3 className='font-semibold text-warning-dark'>
                  {user?.kycStatus === 'Not Started' && 'KYC Verification Required'}
                  {user?.kycStatus === 'Pending' && 'KYC Under Review'}
                  {user?.kycStatus === 'Rejected' && 'KYC Rejected'}
                </h3>
                <p className='text-sm text-gray-700 mt-1'>
                  {user?.kycStatus === 'Not Started' && 
                    'Complete your KYC verification to start investing and make withdrawals.'}
                  {user?.kycStatus === 'Pending' && 
                    'Your KYC documents are under review. We will notify you once approved.'}
                  {user?.kycStatus === 'Rejected' && 
                    'Your KYC verification was rejected. Please resubmit with correct documents.'}
                </p>
                <a 
                  href='/kyc' 
                  className='inline-block mt-2 text-sm font-semibold text-primary hover:text-primary-hover'
                >
                  {user?.kycStatus === 'Not Started' ? 'Complete KYC' : 'View Status'} →
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Disclaimer Banner */}
        <div className='bg-warning-light border-l-4 border-warning p-4 rounded' data-testid='disclaimer-banner-earnings'>
          <p className='text-sm text-gray-700'>
            <strong>Important:</strong> Returns are 'up to' and not guaranteed. Performance varies. 
            Commissions are paid on realized profits only, not on deposits.
          </p>
        </div>

        {/* Metrics Grid */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
          {/* Portfolio Card */}
          <div className='bg-white rounded-lg border border-gray-200 p-6 shadow-sm' data-testid='dashboard-card-portfolio'>
            <div className='flex items-center justify-between mb-4'>
              <div>
                <p className='text-sm font-medium text-gray-600 uppercase tracking-wide'>Portfolio</p>
                <p className='text-xs text-gray-500 mt-1'>Capital Balance</p>
              </div>
              <div className='p-3 bg-primary-light rounded-lg'>
                <Wallet className='w-6 h-6 text-primary' />
              </div>
            </div>
            <p className='text-2xl font-mono font-semibold text-gray-900'>
              {formatIndianCurrency(wallet?.capitalBalance || 0)}
            </p>
            <p className='text-sm text-gray-600 mt-2'>
              Total Deposits: {formatIndianCurrency(totalDeposits)}
            </p>
          </div>

          {/* Earnings Card */}
          <div className='bg-white rounded-lg border border-gray-200 p-6 shadow-sm' data-testid='dashboard-card-profits'>
            <div className='flex items-center justify-between mb-4'>
              <div>
                <p className='text-sm font-medium text-gray-600 uppercase tracking-wide'>Earnings</p>
                <p className='text-xs text-gray-500 mt-1'>This Month</p>
              </div>
              <div className='p-3 bg-success-light rounded-lg'>
                <TrendingUp className='w-6 h-6 text-success' />
              </div>
            </div>
            <p className='text-2xl font-mono font-semibold text-gray-900'>
              {formatIndianCurrency(wallet?.earningsBalance || 0)}
            </p>
            <div className='flex items-center gap-1 mt-2'>
              <ArrowUp className='w-4 h-4 text-success' />
              <span className='text-sm text-success font-medium'>Profit + Commission</span>
            </div>
          </div>

          {/* Network Card */}
          <div className='bg-white rounded-lg border border-gray-200 p-6 shadow-sm' data-testid='dashboard-card-network'>
            <div className='flex items-center justify-between mb-4'>
              <div>
                <p className='text-sm font-medium text-gray-600 uppercase tracking-wide'>Network</p>
                <p className='text-xs text-gray-500 mt-1'>Team Size</p>
              </div>
              <div className='p-3 bg-primary-light rounded-lg'>
                <Users className='w-6 h-6 text-primary' />
              </div>
            </div>
            <p className='text-2xl font-mono font-semibold text-gray-900'>0</p>
            <p className='text-sm text-gray-600 mt-2'>Direct Referrals: 0/5</p>
          </div>

          {/* Next Withdrawal Card */}
          <div className='bg-white rounded-lg border border-gray-200 p-6 shadow-sm' data-testid='dashboard-card-withdrawal'>
            <div className='flex items-center justify-between mb-4'>
              <div>
                <p className='text-sm font-medium text-gray-600 uppercase tracking-wide'>Withdrawal</p>
                <p className='text-xs text-gray-500 mt-1'>Next Window</p>
              </div>
              <div className='p-3 bg-warning-light rounded-lg'>
                <Calendar className='w-6 h-6 text-warning' />
              </div>
            </div>
            <p className='text-2xl font-semibold text-gray-900'>1st</p>
            <p className='text-sm text-gray-600 mt-2'>of every month</p>
          </div>
        </div>

        {/* Live Arbitrage Ticker */}
        <div className='bg-white rounded-lg border border-gray-200 p-6' data-testid='dashboard-card-arbitrage'>
          <div className='flex items-center justify-between mb-4'>
            <div className='flex items-center gap-2'>
              <BarChart3 className='w-5 h-5 text-primary' />
              <h2 className='text-lg font-heading font-semibold text-gray-900'>
                Live Arbitrage Feed
              </h2>
              <span className='inline-flex items-center gap-1 bg-success-light text-success text-xs font-medium px-2 py-1 rounded-full'>
                <span className='w-2 h-2 bg-success rounded-full animate-pulse'></span>
                Live
              </span>
            </div>
            <a href='/arbitrage' className='text-sm font-medium text-primary hover:text-primary-hover'>
              View All →
            </a>
          </div>
          
          {arbitrageData.length > 0 ? (
            <div className='overflow-x-auto'>
              <div className='flex gap-4 pb-2'>
                {arbitrageData.map((arb, idx) => (
                  <div 
                    key={idx}
                    className='flex-shrink-0 bg-surface rounded-lg p-4 min-w-[280px]'
                  >
                    <div className='flex items-center justify-between mb-2'>
                      <span className='font-mono font-semibold text-gray-900'>
                        {arb.token_pair.split('/')[0].slice(0, 6) + '...' || 'N/A'}
                      </span>
                      <span className='text-success font-semibold'>
                        +{(arb.gross_profit_usd / (arb.gross_profit_usd + arb.gas_cost_usd) * 100).toFixed(2)}%
                      </span>
                    </div>
                    <div className='flex items-center justify-between text-sm'>
                      <div>
                        <p className='text-gray-600'>{arb.buy_dex}</p>
                        <p className='font-mono text-gray-900'>
                          {formatIndianCurrency(arb.gross_profit_usd)}
                        </p>
                      </div>
                      <ArrowUp className='w-4 h-4 text-success' />
                      <div className='text-right'>
                        <p className='text-gray-600'>{arb.sell_dex}</p>
                        <p className='font-mono text-gray-900'>
                          {formatIndianCurrency(arb.gross_profit_usd + arb.net_profit_usd)}
                        </p>
                      </div>
                    </div>
                    <p className='text-xs text-gray-500 mt-2'>
                      Status: {arb.status.charAt(0).toUpperCase() + arb.status.slice(1)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className='text-gray-500 text-center py-4'>No arbitrage data available</p>
          )}
          
          <p className='text-xs text-gray-500 mt-4'>
            <strong>Note:</strong> Informational only. No guarantee of execution or returns. Auto-refreshes every 5 seconds.
          </p>
        </div>

        {/* Recent Deposits */}
        <div className='bg-white rounded-lg border border-gray-200 p-6' data-testid='dashboard-deposits-list'>
          <div className='flex items-center justify-between mb-4'>
            <h2 className='text-lg font-heading font-semibold text-gray-900'>Recent Deposits</h2>
            <a href='/deposits' className='text-sm font-medium text-primary hover:text-primary-hover'>
              View All →
            </a>
          </div>
          
          {deposits.length > 0 ? (
            <div className='space-y-4'>
              {deposits.slice(0, 3).map((deposit) => (
                <div 
                  key={deposit.id}
                  className='flex items-center justify-between p-4 bg-surface rounded-lg'
                >
                  <div>
                    <p className='font-semibold text-gray-900'>{deposit.planName}</p>
                    <p className='text-sm text-gray-600 mt-1'>
                      {formatIndianDate(deposit.startDate)}
                    </p>
                  </div>
                  <div className='text-right'>
                    <p className='font-mono font-semibold text-gray-900'>
                      {formatIndianCurrency(deposit.amount)}
                    </p>
                    <span className={`inline-block text-xs font-medium px-2 py-1 rounded mt-1 ${
                      deposit.status === 'active' ? 'bg-success-light text-success' :
                      deposit.status === 'pending' ? 'bg-warning-light text-warning' :
                      'bg-gray-200 text-gray-600'
                    }`}>
                      {deposit.status.charAt(0).toUpperCase() + deposit.status.slice(1)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='text-center py-8'>
              <TrendingUp className='w-12 h-12 text-gray-400 mx-auto mb-3' />
              <p className='text-gray-600'>No deposits yet</p>
              <a 
                href='/plans' 
                className='inline-block mt-3 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover font-medium'
              >
                Start Investing
              </a>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;